import React, { useState, useEffect } from 'react';
import './Gallery.css';
import galleryManifest from '../galleryManifest.json';

type AlbumKey = keyof typeof galleryManifest;

const albums = galleryManifest;

const Gallery: React.FC = () => {
  const [expandedAlbums, setExpandedAlbums] = useState<Set<string>>(new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentAlbum, setCurrentAlbum] = useState<string | null>(null);

  const toggleAlbum = (albumKey: string) => {
    setExpandedAlbums(prev => {
      const newSet = new Set(prev);
      if (newSet.has(albumKey)) {
        newSet.delete(albumKey);
      } else {
        newSet.add(albumKey);
      }
      return newSet;
    });
  };

  const openLightbox = (albumKey: string, photoIndex: number) => {
    setCurrentAlbum(albumKey);
    setCurrentPhotoIndex(photoIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    if (currentAlbum && albums[currentAlbum as AlbumKey]) {
      const photos = albums[currentAlbum as AlbumKey].photos;
      setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
    }
  };

  const prevPhoto = () => {
    if (currentAlbum && albums[currentAlbum as AlbumKey]) {
      const photos = albums[currentAlbum as AlbumKey].photos;
      setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
    }
  };

  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        if (currentAlbum && albums[currentAlbum as AlbumKey]) {
          const photos = albums[currentAlbum as AlbumKey].photos;
          setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
        }
      } else if (e.key === 'ArrowRight') {
        if (currentAlbum && albums[currentAlbum as AlbumKey]) {
          const photos = albums[currentAlbum as AlbumKey].photos;
          setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxOpen, currentAlbum]);

  return (
    <>
      <section className="gallery" id="gallery">
        <div className="gallery-container">
          <h2 className="section-title">Galerie</h2>
          
          <div className="albums-section">
            {Object.entries(albums).map(([key, album]) => {
              const isExpanded = expandedAlbums.has(key);
              return (
                <div key={key} className="album-section">
                  <div 
                    className="album-header"
                    onClick={() => toggleAlbum(key)}
                  >
                    <h3>{album.title}</h3>
                    <p className="album-count">{album.photos.length} fotek</p>
                    <span className="expand-icon">{isExpanded ? '−' : '+'}</span>
                  </div>
                  <div className="album-preview">
                    {album.photos.slice(0, 4).map((photo, index) => (
                      <div 
                        key={index}
                        className="preview-thumb"
                        onClick={() => openLightbox(key, index)}
                      >
                        <img src={photo} alt={`${album.title} ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                  {isExpanded && (
                    <div className="photos-grid">
                      {album.photos.map((photo, index) => (
                        <div 
                          key={index} 
                          className="photo-item"
                          onClick={() => openLightbox(key, index)}
                        >
                          <img src={photo} alt={`${album.title} ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {lightboxOpen && currentAlbum && albums[currentAlbum as AlbumKey] && (
        <div 
          className="lightbox"
          onClick={closeLightbox}
        >
          <button 
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="Zavřít"
          >
            ×
          </button>
          <button 
            className="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            aria-label="Předchozí"
          >
            ‹
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src={albums[currentAlbum as AlbumKey].photos[currentPhotoIndex]} 
              alt={`${albums[currentAlbum as AlbumKey].title} ${currentPhotoIndex + 1}`}
            />
            <div className="lightbox-counter">
              {currentPhotoIndex + 1} / {albums[currentAlbum as AlbumKey].photos.length}
            </div>
          </div>
          <button 
            className="lightbox-next"
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            aria-label="Další"
          >
            ›
          </button>
        </div>
      )}
    </>
  );
};

export default Gallery;

